import { initializeApp, getApps } from 'firebase/app';
import {
  initializeFirestore,
  persistentLocalCache,
  persistentMultipleTabManager,
  getFirestore,
  connectFirestoreEmulator,
} from 'firebase/firestore';
import { getAuth, connectAuthEmulator } from 'firebase/auth';
import { getFunctions, connectFunctionsEmulator } from 'firebase/functions';
import { getStorage, connectStorageEmulator } from 'firebase/storage';
import { initializeAppCheck, ReCaptchaEnterpriseProvider, ReCaptchaV3Provider } from 'firebase/app-check';
import { connectLiteEmulator } from '../data/liteDispatch';
import { META_PIXEL_ID } from '../../marketing/metaPixel.js';

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  // The GA4 web stream's id ("G-…"), given here so the SDK uses it rather
  // than asking Firebase's config endpoint for it.
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID || undefined,
};

const useEmulator = import.meta.env.VITE_USE_EMULATOR === 'true';

// Which emulator suite, when `useEmulator` is on: its project and its ports,
// put here by vite.config.js from CODA_EMULATOR_SUITE
// (scripts/emulator-suites.mjs). Unset — every run but a private one such as
// `npm run videos` — it is `demo-coda` on firebase.json's ports.
const EMULATOR_SUITE = typeof __EMULATOR_SUITE__ === 'object' && __EMULATOR_SUITE__
  ? __EMULATOR_SUITE__
  : { project: 'demo-coda', host: '127.0.0.1', firestore: 8080, auth: 9099, storage: 9199, functions: 5001 };

// In emulator mode the project ID can be anything, so isConfigured is
// true whenever VITE_USE_EMULATOR=true even without real API keys.
export const isConfigured =
  useEmulator ||
  (!!firebaseConfig.apiKey && firebaseConfig.apiKey !== 'your-api-key');

let app;
let db;
let auth;
let functions;
let storage;

if (isConfigured) {
  // Under the emulator, a placeholder config so the SDK does not contact
  // production endpoints. projectId is pinned to the suite's (matching the
  // emulator's --project flag) rather than whatever placeholder is in .env, so
  // callable URLs resolve to the right /<project>/us-central1/… path.
  const config = useEmulator
    ? {
        apiKey: 'emulator',
        projectId: EMULATOR_SUITE.project,
        authDomain: 'localhost',
        // Storage needs a bucket name even against the emulator, or
        // getStorage() cannot build a ref. The emulator accepts the
        // conventional name without the bucket existing.
        storageBucket: `${EMULATOR_SUITE.project}.appspot.com`,
      }
    : firebaseConfig;

  app = getApps().length === 0 ? initializeApp(config) : getApps()[0];

  // App Check: each request carries a token attesting it came from a real
  // instance of the app, since the API key is publicly bundled. Gated on the
  // env var so a deployment without the console-side setup
  // (docs/launch-checklist.md §2, #437) works without it.
  //
  // Straight after the app and before any product is fetched, as the SDK asks,
  // so the first Firestore listener already has a token. The phones do the
  // same in `Backend.kt` and `Backend.swift`.
  //
  // The provider follows the key's kind: reCAPTCHA Enterprise
  // (VITE_RECAPTCHA_ENTERPRISE_SITE_KEY) or v3 (VITE_RECAPTCHA_SITE_KEY);
  // Enterprise wins when both are set. The App Check console must be given the
  // same kind.
  //
  // Skipped under the emulator, which does not verify App Check. When neither
  // key is set at build time, Vite inlines undefined and Rollup tree-shakes the
  // import and the branch.
  //
  // A debug token, on a dev server only: reCAPTCHA will not mint a token on
  // localhost for a key whose domains are the app's, so a dev server pointed at
  // production (`npx vite --mode production`, as `npm run studio -- --env
  // production signin web` uses) would be refused. `VITE_APPCHECK_DEBUG_TOKEN`
  // — a token registered in the App Check console, or `true` to have the SDK
  // make one and print it — swaps in the debug provider.
  // `import.meta.env.DEV` is false in every `vite build`, so the token is not
  // in a built bundle; give it on the command line rather than in an env file.
  const enterpriseSiteKey = import.meta.env.VITE_RECAPTCHA_ENTERPRISE_SITE_KEY;
  const recaptchaSiteKey = import.meta.env.VITE_RECAPTCHA_SITE_KEY;
  if ((enterpriseSiteKey || recaptchaSiteKey) && !useEmulator) {
    if (import.meta.env.DEV && import.meta.env.VITE_APPCHECK_DEBUG_TOKEN) {
      const debugToken = import.meta.env.VITE_APPCHECK_DEBUG_TOKEN;
      globalThis.FIREBASE_APPCHECK_DEBUG_TOKEN = debugToken === 'true' ? true : debugToken;
    }
    initializeAppCheck(app, {
      provider: enterpriseSiteKey
        ? new ReCaptchaEnterpriseProvider(enterpriseSiteKey)
        : new ReCaptchaV3Provider(recaptchaSiteKey),
      isTokenAutoRefreshEnabled: true,
    });
  }

  // This cache is per-app, not per-uid (#302): an IndexedDB database of the
  // SDK's own, holding every document this browser has read for any teacher
  // who signed in here. `claimDevice` in src/context/AuthContext.jsx sweeps it,
  // with our own `coda-app-state`, when the next teacher signs in, in the
  // window before the auth observer publishes a user and anything touches
  // Firestore.
  try {
    db = initializeFirestore(app, {
      localCache: persistentLocalCache({
        tabManager: persistentMultipleTabManager(),
      }),
    });
  } catch {
    db = getFirestore(app);
  }

  auth = getAuth(app);
  // Region must match what the functions declare server-side
  // (functions/index.js → onCall({ region: 'us-central1' })).
  functions = getFunctions(app, 'us-central1');
  // Receipt images and studio files. storage.rules scopes every object to the
  // signed-in studio's own tree and caps size and content type, and both trees
  // need a one-shot reservation minted by the `authorizeUpload` callable (#62),
  // so a compromised session cannot run past the studio's daily allowance and
  // byte ceiling.
  storage = getStorage(app);
  // How long an upload keeps retrying once its connection has failed (#611).
  // The SDK's default is ten minutes per request, spent more than once: a
  // resumable upload (every file over 256 KB) that fails asks the server where
  // it got to, backing off 1, 2, 4, 8, 16 s, each ask with the same allowance.
  // 15 s makes that about 90 s, and a shorter dropout resumes where it stopped.
  // It bounds retries, not transfers (@firebase/storage 0.13's `backoff.ts`):
  // a request still sending bytes is never cut off. A connection that goes
  // silent with its socket open is not bounded by this; the no-progress
  // watchdog (src/lib/uploadWatch.js, #612) handles it on top of this setting.
  // The same 15 s as Android's `Backend.UPLOAD_RETRY_MS` and the iPhone's
  // `Backend.uploadRetrySeconds`, which bounds less (Backend.swift).
  storage.maxUploadRetryTime = 15_000;

  if (useEmulator) {
    const { host, firestore, auth: authPort, functions: functionsPort, storage: storagePort } = EMULATOR_SUITE;
    connectFirestoreEmulator(db, host, firestore);
    // The write path's Firestore is a second instance (#410):
    // `firebase/firestore/lite` registers under its own component name and
    // knows nothing about the line above. `src/data/liteDispatch.js` owns it.
    //
    // Unconditional, not behind `liteWriteFlag.js`: that flag is a
    // `localStorage` value flipped in a console, and if this depended on it,
    // flipping it in a dev browser would point writes at production while reads
    // stayed on the emulator.
    connectLiteEmulator(host, firestore);
    connectAuthEmulator(auth, `http://${host}:${authPort}`, { disableWarnings: true });
    connectFunctionsEmulator(functions, host, functionsPort);
    connectStorageEmulator(storage, host, storagePort);
  }
}

// What src/lib/analytics.js is wired with, in src/main.jsx (#458): the id,
// when one is configured and this is not the emulator, and null otherwise, in
// which case the Analytics SDK is never loaded.
const analyticsMeasurementId = app && !useEmulator ? firebaseConfig.measurementId || null : null;

// What src/lib/adConversions.js is wired with, in src/main.jsx (#754): the
// Google Ads tag id and the two conversion actions' labels — none against the
// emulator, and an unset id means the Ads tag is never loaded. Read here
// because `import.meta.env` is a parse error under Jest anywhere else in src/.
const googleAds = !useEmulator && import.meta.env.VITE_GOOGLE_ADS_ID
  ? {
    adsId: import.meta.env.VITE_GOOGLE_ADS_ID,
    signUpLabel: import.meta.env.VITE_GOOGLE_ADS_SIGNUP_LABEL || null,
    paidLabel: import.meta.env.VITE_GOOGLE_ADS_PAID_LABEL || null,
  }
  : null;

// The Meta Pixel's id (#780, src/lib/metaPixel.js): public, so it is written
// in marketing/metaPixel.js rather than read from the env, and given only to a
// production build that is not against the emulator.
const metaPixelId = !useEmulator && import.meta.env.PROD ? META_PIXEL_ID : null;

export { app, db, auth, functions, storage, analyticsMeasurementId, googleAds, metaPixelId };
